import barbarianAdapter from "./barbarian";
import rogueAdapter from "./rogue";
import rangerAdapter from "./ranger";
import paladinAdapter from "./paladin";
import clericAdapter from "./cleric";
import druidAdapter from "./druid";
import wizardAdapter from "./wizard";
import warlockAdapter from "./warlock";
import featAdapters from "./feats";
import { normalizeClassId } from "./shared/getClassLevel";
import { getSubclassFeatureEntitlements } from "../../../shared/rules/subclasses/index.mjs";

const subclassFeatureAdapters = Object.freeze({
  'rogue:thief:fast-hands': Object.freeze({
    id: 'rogue:thief:fast-hands',
    source: Object.freeze({ type: 'subclass', classId: 'rogue', subclassId: 'thief' }),
    presentation: Object.freeze({ kind: 'bonus-action', automation: 'dm-adjudicated' }),
  }),
});

/** Build a registry whose explicit array order is its hook evaluation order. */
// Feat adapters ({ id, appliesTo(character), ...hooks }) follow the class adapters and apply when the character has the feat.
export const createClassFeatureRegistry = (adapters = [], feats = []) => {
  const registered = [];
  const adaptersById = new Map();

  adapters.forEach((adapter) => {
    const id = normalizeClassId(adapter?.id);
    if (!id) throw new Error("Class feature adapter id is required.");
    if (typeof adapter?.getClassLevel !== "function") {
      throw new Error(`Class feature adapter "${id}" must implement getClassLevel().`);
    }
    if (adaptersById.has(id)) {
      throw new Error(`Duplicate class feature adapter id: "${id}".`);
    }

    const normalizedAdapter = Object.freeze({ ...adapter, id });
    registered.push(normalizedAdapter);
    adaptersById.set(id, normalizedAdapter);
  });

  const getClassAdapter = (classId) =>
    adaptersById.get(normalizeClassId(classId)) || null;
  const getApplicableClassAdapters = (character) =>
    registered.filter((adapter) => adapter.getClassLevel(character) > 0);
  const getClassFeatureHooks = (character, hookName) =>
    [...getApplicableClassAdapters(character), ...feats.filter((feat) => feat.appliesTo(character))]
      .map((adapter) => adapter?.[hookName])
      .filter((hook) => typeof hook === "function");
  const getApplicableFeatureAdapters = (character) => [
    ...getApplicableClassAdapters(character),
    ...getSubclassFeatureEntitlements(character)
      .map(({ id }) => subclassFeatureAdapters[id])
      .filter(Boolean),
  ];

  return Object.freeze({
    getClassAdapter,
    getApplicableClassAdapters,
    getClassFeatureHooks,
    getApplicableFeatureAdapters,
  });
};

/** Compose discovered hooks without taking ownership of their result contract. */
export const createHitFeatureEvaluator = (hooks, queries) => {
  if (!Array.isArray(hooks) || hooks.length === 0) return undefined;

  return async (context) => {
    const evaluations = [];
    for (const hook of hooks) {
      const result = await hook(context, queries);
      if (Array.isArray(result)) evaluations.push(...result);
      else evaluations.push(result);
    }
    return evaluations;
  };
};

const classFeatureRegistry = createClassFeatureRegistry([barbarianAdapter, rogueAdapter, rangerAdapter, paladinAdapter, clericAdapter, druidAdapter, wizardAdapter, warlockAdapter], featAdapters);

export const {
  getClassAdapter,
  getApplicableClassAdapters,
  getClassFeatureHooks,
  getApplicableFeatureAdapters,
} = classFeatureRegistry;

export default classFeatureRegistry;

